import Decimal from 'decimal.js';
import { Entity } from '../entity';
import { PaymentEditorInfo, PaymentMethod, PaymentMethodEntity, PaymentMethodType, PaymentType } from '../payment/payment';
import { InvoiceCalculationDomain } from './invoice.calculation.domain';
import { Bill, Invoice } from './invoicing';
import { InvoiceStatus } from './invoicing.enum';

/** Opening the payment dialog: what the document owes, decided by the server rather than the browser. */
export interface FinancialDocumentPaymentPrepareRequest {
    documentId: string;
}

/** Present only for an invoice receipt whose billing company has Stripe connected. */
export interface FinancialDocumentCardPaymentOptions {
    publicKey: string;
    method: PaymentMethodEntity;
}

/** `EntityItemRequest.options` key for a financial document's payment options; the answer comes back under the same key. */
export const FINANCIAL_DOCUMENT_PAYMENT_OPTION = 'payment';

/**
 * Which payment actions the loaded document can offer the reader, computed on read when asked for
 * (`FINANCIAL_DOCUMENT_PAYMENT_OPTION`) so the header shows its final set from first paint. Never stored.
 * Advisory only: each dialog asks the server again when opened.
 */
export interface FinancialDocumentPaymentOptions {
    /** An invoice receipt whose billing company has Stripe connected. */
    card: boolean;
    /** Credit the account can allocate against this document (sum available on the opposite side); 0 for none. */
    credit: number;
}

export interface FinancialDocumentPaymentPreflight {
    info: PaymentEditorInfo | null;
    card: FinancialDocumentCardPaymentOptions | null;
}

/** A manual payment, refund or prepayment receipt recorded against one invoice or bill. */
export interface FinancialDocumentPaymentRecordRequest {
    documentId: string;
    /** The type the dialog was opened for; a different server answer means the document moved underneath it. */
    type: PaymentType;
    amount: number;
    date: Date | string;
    method: PaymentMethodEntity;
    receiver?: Entity | null;
    payer?: string | null;
    reference?: string | null;
    note?: string | null;
    timezone?: string | null;
}

/** Card step 1: charge this much; reopening the dialog reuses the invoice's open intent. */
export interface FinancialDocumentCardIntentRequest {
    documentId: string;
    amount: number;
}

export interface FinancialDocumentCardIntent {
    intentId: string;
    clientSecret: string;
    amount: number;
}

/** Card step 2: the browser only names the intent; the server reads the charge from Stripe itself. */
export interface FinancialDocumentCardPaymentRecordRequest {
    documentId: string;
    intentId: string;
    reference?: string | null;
    payer?: string | null;
}

/** Stripe's smallest chargeable amount in AUD. */
export const CARD_PAYMENT_MINIMUM = 0.5;

/**
 * True when `amount` is at least one cent. Finance Pro uses this to hide Refund once remaining credit
 * is only a float residue, and the payment dialog and `decideFinancialDocumentPayment` use it to refuse
 * a posted amount under 0.01. A normal refund such as 0.50 passes. Hundredths are not rounded, so 0.009
 * stays under one cent; rounding them would accept it.
 */
export function isAtLeastOneCent(amount: number): boolean {

    if (!Number.isFinite(amount)) return false;
    return new Decimal(amount).mul(100).greaterThanOrEqualTo(1);
}

export type FinancialDocumentPaymentDecision =
    | { ok: true; info: PaymentEditorInfo }
    | { ok: false; reason: string };

/**
 * The one rule set both data strategies apply before a payment is written. The legacy endpoints save
 * an over-limit payment as a `WorkBuddyError` record; this refuses it instead, so nothing is written.
 */
export function decideFinancialDocumentPayment(document: Invoice | Bill | null, isBill: boolean, request: Pick<FinancialDocumentPaymentRecordRequest, 'type' | 'amount' | 'date' | 'method'>): FinancialDocumentPaymentDecision {

    if (!document?._id) return { ok: false, reason: `${isBill ? 'Bill' : 'Invoice'} not found` };
    if (document.status === InvoiceStatus.Void) return { ok: false, reason: `A void ${isBill ? 'bill' : 'invoice'} cannot take payments` };

    const info = InvoiceCalculationDomain.from(document, isBill).getPaymentInfo();
    if (!info) return { ok: false, reason: `This ${isBill ? 'bill' : 'invoice'} has nothing to pay or refund` };
    if (info.type !== request.type) return { ok: false, reason: `This ${isBill ? 'bill' : 'invoice'} has changed. Reload it and try again.` };

    const amount = Number(request.amount);
    if (!Number.isFinite(amount) || amount <= 0) return { ok: false, reason: 'Enter an amount greater than zero' };
    // A used-up credit note can still report a positive residue (1e-15). That is not a refund; 0.50 is.
    if (!isAtLeastOneCent(amount)) return { ok: false, reason: 'Enter at least 0.01' };
    // Cents, not floats: the cap is a currency value and float subtraction would refuse the exact amount.
    const cents = new Decimal(amount).mul(100).toDecimalPlaces(0);
    const owed = new Decimal(info.amount).mul(100).toDecimalPlaces(0);
    if (cents.greaterThan(owed)) return { ok: false, reason: `The amount cannot exceed ${owed.div(100).toFixed(2)}` };
    if (!info.amountEditable && !cents.equals(owed)) return { ok: false, reason: `This payment must be the full ${owed.div(100).toFixed(2)}` };

    if (!request.date || Number.isNaN(new Date(request.date).getTime())) return { ok: false, reason: 'Choose a date' };
    if (!request.method?._id) return { ok: false, reason: 'Choose a payment method' };

    return { ok: true, info };
}

/** A manual method the document's company may record against; a method with no companies is offered by all. */
export function isManualPaymentMethodFor(method: PaymentMethod | null | undefined, companyId: string | undefined): boolean {

    if (!method || method.active === false || method.type !== PaymentMethodType.Manual) return false;
    if (!method.companies?.length) return true;
    return !!companyId && method.companies.some(company => company?._id === companyId);
}

/** The parts of a Stripe PaymentIntent the verification reads; kept structural so the model needs no Stripe types. */
export interface CardPaymentIntentFacts {
    id: string;
    status: string;
    amount: number;
    currency: string;
    metadata?: Record<string, string> | null;
}

/**
 * Why a Stripe intent must not be recorded against this invoice, or null when it is a completed AUD
 * charge raised for it. The browser's word that a card was charged is never enough on its own.
 */
export function cardIntentRefusal(intent: CardPaymentIntentFacts | null | undefined, invoiceId: string): string | null {

    if (!intent?.id) return 'The card payment could not be found';
    if (intent.metadata?.['invoiceId'] !== invoiceId) return 'This card payment belongs to a different invoice';
    if (intent.currency?.toLowerCase() !== 'aud') return 'This card payment is not in AUD';
    if (intent.status !== 'succeeded') return 'The card has not been charged';
    if (!(intent.amount > 0)) return 'The card payment has no amount';
    return null;
}
